import React, { useState } from 'react';

/** A row of horizontal magnitude bars with a per-bar hover tooltip.
 * `colorFor(value)` lets the caller apply status color (e.g. pass rate)
 * instead of the single default hue — pass nothing for a plain sequential
 * (one-hue) chart. Every value is also direct-labeled at the bar's end, so
 * color is never the only channel carrying it. `max` scales the bars —
 * defaults to 100 for percentages; pass the data's own max (or omit and
 * let it auto-scale to the largest bar) for raw counts. */
export default function HorizontalBarChart({ bars, colorFor, valueSuffix = '%', emptyLabel, max }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  if (!bars || bars.length === 0) {
    return <p className="text-sm text-gray-500">{emptyLabel}</p>;
  }

  const scaleMax = max ?? Math.max(100, ...bars.map((b) => b.value || 0));

  return (
    <div className="space-y-2.5">
      {bars.map((bar, idx) => {
        const hasValue = bar.value !== null && bar.value !== undefined;
        const pct = hasValue ? Math.max(0, Math.min(100, (bar.value / scaleMax) * 100)) : 0;
        const color = colorFor ? colorFor(bar.value) : '#6739e0';
        const displayValue = hasValue ? `${bar.value}${valueSuffix}` : '—';
        return (
          <div key={bar.label} className="flex items-center gap-3">
            <span className="w-28 sm:w-36 shrink-0 text-xs text-gray-600 truncate text-right" title={bar.label}>
              {bar.label}
            </span>
            <div
              className="relative flex-1 h-3 bg-gray-100"
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              onFocus={() => setHoveredIdx(idx)}
              onBlur={() => setHoveredIdx(null)}
              tabIndex={0}
              role="img"
              aria-label={`${bar.label}: ${displayValue}`}
            >
              <div
                className="h-full rounded-r transition-all duration-300"
                style={{ width: `${pct}%`, backgroundColor: hasValue ? color : 'transparent' }}
              />
              {hoveredIdx === idx && (
                <div
                  className="absolute -top-8 -translate-x-1/2 bg-gray-900 text-white text-xs font-medium px-2 py-1 rounded whitespace-nowrap z-10 pointer-events-none"
                  style={{ left: `${Math.max(6, Math.min(94, pct))}%` }}
                >
                  {bar.label}: {displayValue}
                </div>
              )}
            </div>
            <span className="w-12 shrink-0 text-xs text-gray-700 font-medium tabular-nums">{displayValue}</span>
          </div>
        );
      })}
    </div>
  );
}
